import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Milestone, Search, Plus, CheckCircle, Clock, X } from 'lucide-react';
import { toast } from 'react-hot-toast';

const MilestoneBillingPage = () => {
  const [plans, setPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [newPlan, setNewPlan] = useState({
    sales_order_number: '',
    created_by: 'USER',
    milestones: [
      { milestone_id: 'M1', description: '', percentage: 0, billing_value: 0, target_date: '' }
    ]
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const fetchPlans = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/milestone-billing/plans`);
      const data = await response.json();
      setPlans(data);
    } catch (error) {
      toast.error('Failed to load milestone plans');
    }
  }, [backendUrl]);

  const filterPlans = useCallback(() => {
    let filtered = plans;
    if (searchTerm) {
      filtered = filtered.filter(plan => 
        plan.sales_order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plan.plan_id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredPlans(filtered);
  }, [plans, searchTerm]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    filterPlans();
  }, [filterPlans]);

  const addMilestone = () => {
    const nextMilestoneNum = newPlan.milestones.length + 1;
    setNewPlan({
      ...newPlan,
      milestones: [
        ...newPlan.milestones,
        { 
          milestone_id: `M${nextMilestoneNum}`, 
          description: '', 
          percentage: 0, 
          billing_value: 0, 
          target_date: '' 
        }
      ]
    });
  };

  const removeMilestone = (index) => {
    const updatedMilestones = newPlan.milestones.filter((_, i) => i !== index);
    setNewPlan({ ...newPlan, milestones: updatedMilestones });
  };

  const updateMilestone = (index, field, value) => {
    const updatedMilestones = [...newPlan.milestones];
    updatedMilestones[index][field] = field === 'percentage' || field === 'billing_value' ? parseFloat(value) || 0 : value;
    setNewPlan({ ...newPlan, milestones: updatedMilestones });
  };

  const handleCreatePlan = async () => {
    if (!newPlan.sales_order_number) {
      toast.error('Please enter sales order number');
      return;
    }

    const totalPercentage = newPlan.milestones.reduce((sum, m) => sum + m.percentage, 0);
    if (Math.abs(totalPercentage - 100) > 0.01) {
      toast.error(`Total percentage must be 100%. Current: ${totalPercentage.toFixed(2)}%`);
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/milestone-billing/plans`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPlan)
      });

      if (response.ok) {
        toast.success('Milestone plan created successfully');
        setShowCreateDialog(false);
        fetchPlans();
        setNewPlan({
          sales_order_number: '',
          created_by: 'USER',
          milestones: [
            { milestone_id: 'M1', description: '', percentage: 0, billing_value: 0, target_date: '' }
          ]
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create milestone plan');
      }
    } catch (error) {
      toast.error('Error creating milestone plan');
    }
  };

  const handleAchieveMilestone = async (planId, milestoneId) => {
    if (!window.confirm(`Mark milestone ${milestoneId} as achieved?`)) return;

    try {
      const response = await fetch(
        `${backendUrl}/api/milestone-billing/plans/${planId}/milestones/${milestoneId}/achieve?achieved_by=USER`,
        { method: 'POST' }
      );

      if (response.ok) {
        toast.success('Milestone marked as achieved');
        fetchPlans();
        if (selectedPlan && selectedPlan.plan_id === planId) {
          const updatedPlan = await response.json();
          setSelectedPlan(updatedPlan);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to achieve milestone');
      }
    } catch (error) {
      toast.error('Error achieving milestone');
    }
  };

  const viewPlanDetails = (plan) => {
    setSelectedPlan(plan);
    setShowDetailsDialog(true);
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Achieved': 'bg-green-100 text-green-800',
      'Billed': 'bg-orange-100 text-orange-800'
    };
    return <Badge className={colors[status] || 'bg-gray-100 text-gray-800'}>{status}</Badge>;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
  };

  const totalPercentage = newPlan.milestones.reduce((sum, m) => sum + m.percentage, 0);

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Milestone Billing</h1>
            <p className="text-gray-600">Manage project-based billing milestones</p>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-orange-600 hover:bg-orange-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Milestone Plan
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Milestone Billing Plan</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Sales Order Number *</label>
                  <Input
                    value={newPlan.sales_order_number}
                    onChange={(e) => setNewPlan({...newPlan, sales_order_number: e.target.value})}
                    placeholder="Enter sales order number"
                  />
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold">Milestones</h3>
                    <div className="text-sm">
                      Total: <span className={totalPercentage === 100 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                        {totalPercentage.toFixed(2)}%
                      </span>
                      {totalPercentage !== 100 && <span className="text-red-600 ml-2">(Must equal 100%)</span>}
                    </div>
                  </div>

                  {newPlan.milestones.map((milestone, index) => (
                    <div key={index} className="border rounded p-3 mb-3 bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-medium text-sm">{milestone.milestone_id}</span>
                        {newPlan.milestones.length > 1 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeMilestone(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="col-span-2">
                          <label className="block text-xs mb-1">Description *</label>
                          <Input
                            value={milestone.description}
                            onChange={(e) => updateMilestone(index, 'description', e.target.value)}
                            placeholder="Milestone description"
                          />
                        </div>
                        <div>
                          <label className="block text-xs mb-1">Percentage *</label>
                          <Input
                            type="number"
                            value={milestone.percentage}
                            onChange={(e) => updateMilestone(index, 'percentage', e.target.value)}
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <label className="block text-xs mb-1">Billing Value *</label>
                          <Input
                            type="number"
                            value={milestone.billing_value}
                            onChange={(e) => updateMilestone(index, 'billing_value', e.target.value)}
                            placeholder="0.00"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-xs mb-1">Target Date</label>
                          <Input
                            type="date"
                            value={milestone.target_date}
                            onChange={(e) => updateMilestone(index, 'target_date', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <Button variant="outline" onClick={addMilestone} className="w-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Milestone
                  </Button>
                </div>

                <div className="flex justify-end space-x-2 mt-6 pt-4 border-t">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                  <Button onClick={handleCreatePlan}>Create Plan</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by SO or Plan ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Plans Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan ID</TableHead>
                <TableHead>Sales Order</TableHead>
                <TableHead className="text-right">Total Milestones</TableHead>
                <TableHead className="text-right">Achieved</TableHead>
                <TableHead className="text-right">Billed</TableHead>
                <TableHead className="text-right">Total Value</TableHead>
                <TableHead className="text-right">Billed Value</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPlans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                    <Milestone className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <div>No milestone plans found. Click "Create Milestone Plan" to get started.</div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPlans.map((plan) => (
                  <TableRow key={plan.plan_id}>
                    <TableCell className="font-medium">{plan.plan_id}</TableCell>
                    <TableCell>{plan.sales_order_number}</TableCell>
                    <TableCell className="text-right">{plan.total_milestones}</TableCell>
                    <TableCell className="text-right">{plan.achieved_milestones}</TableCell>
                    <TableCell className="text-right">{plan.billed_milestones}</TableCell>
                    <TableCell className="text-right">{formatCurrency(plan.total_value)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(plan.billed_value)}</TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => viewPlanDetails(plan)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Plan Details Dialog */}
        <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Milestone Plan Details - {selectedPlan?.plan_id}</DialogTitle>
            </DialogHeader>
            {selectedPlan && (
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Sales Order</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-lg font-semibold">{selectedPlan.sales_order_number}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Progress</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-lg font-semibold">
                        {selectedPlan.achieved_milestones} / {selectedPlan.total_milestones} Achieved
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="border rounded-lg">
                  <div className="p-4 bg-gray-50 border-b">
                    <h3 className="font-semibold">Milestones</h3>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">%</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                        <TableHead>Target Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedPlan.milestones.map((milestone) => (
                        <TableRow key={milestone.milestone_id}>
                          <TableCell className="font-medium">{milestone.milestone_id}</TableCell>
                          <TableCell>{milestone.description}</TableCell>
                          <TableCell className="text-right">{milestone.percentage}%</TableCell>
                          <TableCell className="text-right">{formatCurrency(milestone.billing_value)}</TableCell>
                          <TableCell>{milestone.target_date || '-'}</TableCell>
                          <TableCell>{getStatusBadge(milestone.status)}</TableCell>
                          <TableCell>
                            {milestone.status === 'Pending' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleAchieveMilestone(selectedPlan.plan_id, milestone.milestone_id)}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Achieve
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredPlans.length} of {plans.length} milestone plans
        </div>
      </div>
    </Layout>
  );
};

export default MilestoneBillingPage;